import React, { useEffect, useMemo, useState } from "react";
import Header from "./Header";

const API_BASE = "https://3d8d71qhr3.execute-api.ap-south-1.amazonaws.com/Prod";

const formatDate = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleString();
};

const safe = (v, fallback = "-") =>
  v === null || v === undefined || v === "" ? fallback : String(v);

const DELIVERY_STATUSES = [
  "pending",
  "processing",
  "packed",
  "shipped",
  "out_for_delivery",
  "delivered",
  "cancelled",
  "returned",
];

function statusBadgeClass(status) {
  const s = String(status || "").toLowerCase();
  if (s === "delivered") return "bg-success";
  if (s === "cancelled" || s === "returned") return "bg-danger";
  if (s === "shipped" || s === "out_for_delivery") return "bg-primary";
  if (s === "processing" || s === "packed") return "bg-warning text-dark";
  return "bg-secondary";
}

/* ── Bootstrap numbered pagination ── */
function Pagination({ page, totalPages, onPageChange, disabled }) {
  if (totalPages <= 1) return null;

  const getPages = () => {
    const pages = [];
    const delta = 2;
    const left = Math.max(2, page - delta);
    const right = Math.min(totalPages - 1, page + delta);

    pages.push(1);
    if (left > 2) pages.push("...");
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPages - 1) pages.push("...");
    if (totalPages > 1) pages.push(totalPages);
    return pages;
  };

  return (
    <nav
      aria-label="Page navigation"
      className="mt-3 d-flex justify-content-center"
    >
      <ul className="pagination pagination-sm mb-0 flex-wrap justify-content-center gap-1">
        <li className={`page-item ${page <= 1 || disabled ? "disabled" : ""}`}>
          <button
            className="page-link rounded-3"
            onClick={() => onPageChange(page - 1)}
          >
            <i className="bi bi-chevron-left" />
          </button>
        </li>

        {getPages().map((p, i) =>
          p === "..." ? (
            <li key={`ellipsis-${i}`} className="page-item disabled">
              <span className="page-link border-0 bg-transparent">…</span>
            </li>
          ) : (
            <li key={p} className={`page-item ${p === page ? "active" : ""}`}>
              <button
                className="page-link rounded-3"
                onClick={() => onPageChange(p)}
                disabled={disabled}
              >
                {p}
              </button>
            </li>
          ),
        )}

        <li
          className={`page-item ${page >= totalPages || disabled ? "disabled" : ""}`}
        >
          <button
            className="page-link rounded-3"
            onClick={() => onPageChange(page + 1)}
          >
            <i className="bi bi-chevron-right" />
          </button>
        </li>
      </ul>
    </nav>
  );
}

function AdminDeliveries() {
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  /* filter — separate input state from applied state to fix clear bug */
  const [userIdInput, setUserIdInput] = useState("");
  const [appliedUserId, setAppliedUserId] = useState("");

  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);

  const [statusDraft, setStatusDraft] = useState({});
  const [updatingId, setUpdatingId] = useState("");

  const adminToken = localStorage.getItem("adminToken");

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((total || 0) / limit)),
    [total, limit],
  );

  const fetchDeliveries = async (nextPage = 1, userId = appliedUserId) => {
    setLoading(true);
    setErrorMsg("");

    if (!adminToken) {
      setErrorMsg("Admin session missing. Please login again.");
      setLoading(false);
      return;
    }

    try {
      const qs = new URLSearchParams({
        page: String(nextPage),
        limit: String(limit),
      });
      if (userId.trim()) qs.set("userId", userId.trim());

      const res = await fetch(
        `${API_BASE}/Deliveries/GetDeliveries?${qs.toString()}`,
        { headers: { Authorization: adminToken } },
      );

      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(txt || `Request failed (${res.status})`);
      }

      const result = await res.json();
      setDeliveries(result?.deliveries || []);
      setTotal(result?.total || 0);
      setPage(result?.page || nextPage);

      // preload status drafts
      const nextDraft = {};
      (result?.deliveries || []).forEach((d) => {
        nextDraft[d.deliveryId] = d.status || "pending";
      });
      setStatusDraft(nextDraft);
    } catch (err) {
      setErrorMsg(err?.message || "Failed to fetch deliveries.");
      setDeliveries([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  /* initial load */
  useEffect(() => {
    fetchDeliveries(1, "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleApply = () => {
    setAppliedUserId(userIdInput);
    setPage(1);
    fetchDeliveries(1, userIdInput);
  };

  const handleClear = () => {
    setUserIdInput("");
    setAppliedUserId("");
    setPage(1);
    fetchDeliveries(1, "");
  };

  const handlePageChange = (p) => {
    setPage(p);
    fetchDeliveries(p, appliedUserId);
  };

  const updateDeliveryStatus = async (deliveryId) => {
    const nextStatus = statusDraft[deliveryId];
    if (!deliveryId || !nextStatus) return;

    setUpdatingId(deliveryId);
    try {
      const res = await fetch(`${API_BASE}/Deliveries/UpdateDelivery`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: adminToken,
        },
        body: JSON.stringify({ deliveryId, status: nextStatus }),
      });

      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(txt || "Update failed");
      }

      fetchDeliveries(page, appliedUserId);
    } catch (err) {
      alert(err?.message || "Update failed");
    } finally {
      setUpdatingId("");
    }
  };

  return (
    <>
      <Header />

      <main className="pb-4">
        <section className="py-4">
          <div className="container d-flex justify-content-center">
            <div className="col-12">
              {/* Header */}
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <h3 className="m-0 fw-semibold lh-1">Deliveries ({total})</h3>
                  <div className="text-muted">
                    All Orders Delivery Status / Update Delivery Status
                  </div>
                </div>

                <div
                  className="icon-shape icon-md bg-light-primary rounded-circle cursor-pointer"
                  onClick={() => fetchDeliveries(page, appliedUserId)}
                  title="Refresh"
                >
                  {loading ? (
                    <span className="spinner-border spinner-border-sm text-dark" />
                  ) : (
                    <i className="bi bi-arrow-clockwise fs-3 text-primary" />
                  )}
                </div>
              </div>

              {/* ── Professional Filter Card ── */}
              <div
                className="card border-0 shadow-sm rounded-4 mb-3"
                style={{
                  background:
                    "linear-gradient(135deg, #f8f9ff 0%, #f0f4ff 100%)",
                }}
              >
                <div className="card-body px-4 py-3">
                  <div className="row g-2 align-items-end">
                    <div className="col-12 col-md">
                      <label
                        className="form-label fw-medium mb-1"
                        style={{ fontSize: 13 }}
                      >
                        <i className="bi bi-person me-1 text-muted" />
                        User ID
                      </label>
                      <div className="input-group">
                        <span className="input-group-text bg-white border-end-0">
                          <i
                            className="bi bi-search text-muted"
                            style={{ fontSize: 13 }}
                          />
                        </span>
                        <input
                          type="text"
                          className="form-control border-start-0 ps-0"
                          value={userIdInput}
                          onChange={(e) => setUserIdInput(e.target.value)}
                          onKeyDown={(e) => e.key === "Enter" && handleApply()}
                          placeholder="Search by User ID…"
                          disabled={loading}
                          style={{ fontSize: 14 }}
                        />
                      </div>
                    </div>

                    <div className="col-12 col-md-auto d-flex gap-2">
                      <button
                        className="btn btn-primary px-4 fw-medium"
                        style={{ borderRadius: 10, fontSize: 14 }}
                        disabled={loading}
                        onClick={handleApply}
                      >
                        <i className="bi bi-check2 me-1" />
                        Apply
                      </button>
                      <button
                        className="btn btn-outline-secondary px-4 fw-medium"
                        style={{ borderRadius: 10, fontSize: 14 }}
                        disabled={loading || (!userIdInput && !appliedUserId)}
                        onClick={handleClear}
                      >
                        <i className="bi bi-x-lg me-1" />
                        Clear
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {errorMsg ? (
                <div className="alert alert-danger rounded-4">{errorMsg}</div>
              ) : null}

              {/* Table */}
              <div className="card shadow-sm rounded-4">
                <div className="card-body p-0">
                  <div className="table-responsive">
                    <table className="table mb-0 align-middle">
                      <thead className="table-light">
                        <tr>
                          <th>No.</th>
                          <th>Details</th>
                          <th>Action</th>
                        </tr>
                      </thead>

                      <tbody>
                        {loading ? (
                          <tr>
                            <td colSpan="3" className="text-center py-5">
                              <span className="spinner-border spinner-border-sm" />
                            </td>
                          </tr>
                        ) : deliveries.length === 0 ? (
                          <tr>
                            <td
                              colSpan="3"
                              className="text-center py-5 text-muted"
                            >
                              No deliveries found.
                            </td>
                          </tr>
                        ) : (
                          deliveries.map((d, idx) => {
                            // API field is `address`, not `shippingAddress`
                            const addr = d.address || {};
                            const addrText = [
                              addr.addressLine1,
                              addr.addressLine2,
                              addr.city,
                              addr.state,
                              addr.zipcode,
                            ]
                              .filter(Boolean)
                              .join(", ");

                            const latestTrack =
                              Array.isArray(d.trackingHistory) &&
                              d.trackingHistory.length
                                ? d.trackingHistory[
                                    d.trackingHistory.length - 1
                                  ]
                                : null;

                            const lineItems = Array.isArray(d.items)
                              ? d.items
                              : [];

                            return (
                              <tr key={d.deliveryId || idx}>
                                <td className="fw-semibold">
                                  {(page - 1) * limit + idx + 1}.
                                </td>

                                <td>
                                  <div className="lh-sm my-2">
                                    <div>
                                      <span className="text-muted">
                                        Delivery ID
                                      </span>{" "}
                                      —{" "}
                                      <span className="fw-semibold">
                                        {safe(d.deliveryId)}
                                      </span>
                                    </div>
                                    <div>
                                      <span className="text-muted">
                                        User ID
                                      </span>{" "}
                                      — {safe(d.userId)}
                                    </div>
                                    <div>
                                      <span className="text-muted">
                                        Name
                                      </span>{" "}
                                      — {safe(d.name)}
                                    </div>

                                    <div className="mt-2 mb-1 fw-semibold text-dark">
                                      Items ({lineItems.length})
                                    </div>
                                    {lineItems.length ? (
                                      <div className="border rounded-3 mb-2 overflow-hidden">
                                        <table className="table table-sm mb-0">
                                          <thead className="table-light">
                                            <tr>
                                              <th>Product</th>
                                              <th>Product ID</th>
                                              <th>Qty</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {lineItems.map((li, liIdx) => (
                                              <tr key={li.productId + liIdx}>
                                                <td className="text-capitalize">
                                                  {safe(
                                                    li.productSnapshot?.name,
                                                    "—",
                                                  )}
                                                </td>
                                                <td>{safe(li.productId)}</td>
                                                <td>
                                                  {safe(li.quantity, "0")}
                                                </td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    ) : null}

                                    <div>
                                      <span className="text-muted">
                                        Payment ID
                                      </span>{" "}
                                      — {safe(d.paymentId)}
                                    </div>
                                    <div>
                                      <span className="text-muted">
                                        Order ID
                                      </span>{" "}
                                      — {safe(d.orderId)}
                                    </div>
                                    <div>
                                      <span className="text-muted">
                                        Razorpay Order
                                      </span>{" "}
                                      — {safe(d.razorpay_order_id)}
                                    </div>
                                    <div>
                                      <span className="text-muted">
                                        Razorpay Payment
                                      </span>{" "}
                                      — {safe(d.razorpay_payment_id)}
                                    </div>
                                    <div>
                                      <span className="text-muted">
                                        Delivery Type
                                      </span>{" "}
                                      — {safe(d.deliveryType)}
                                    </div>
                                    <div>
                                      <span className="text-muted">Status</span>{" "}
                                      —{" "}
                                      <span
                                        className={`badge ${statusBadgeClass(d.status)}`}
                                      >
                                        {safe(d.status)}
                                      </span>
                                    </div>
                                    <div>
                                      <span className="text-muted">
                                        Address
                                      </span>{" "}
                                      — {addrText || "-"}
                                    </div>
                                    <div>
                                      <span className="text-muted">Phone</span>{" "}
                                      — {safe(addr.phone)}
                                    </div>
                                    {latestTrack ? (
                                      <div>
                                        <span className="text-muted">
                                          Latest Tracking
                                        </span>{" "}
                                        — {safe(latestTrack.stage)} /{" "}
                                        {safe(latestTrack.location)} /{" "}
                                        {formatDate(latestTrack.timestamp)}
                                      </div>
                                    ) : null}
                                    <div>
                                      <span className="text-muted">
                                        Created At
                                      </span>{" "}
                                      — {formatDate(d.createdAt)}
                                    </div>
                                    <div>
                                      <span className="text-muted">
                                        Updated At
                                      </span>{" "}
                                      — {formatDate(d.updatedAt)}
                                    </div>
                                  </div>
                                </td>

                                <td>
                                  <div className="d-flex flex-column gap-2 align-items-start py-2">
                                    <select
                                      className="form-select form-select-sm"
                                      style={{ width: 200 }}
                                      value={
                                        statusDraft[d.deliveryId] || d.status
                                      }
                                      onChange={(e) =>
                                        setStatusDraft((prev) => ({
                                          ...prev,
                                          [d.deliveryId]: e.target.value,
                                        }))
                                      }
                                      disabled={updatingId === d.deliveryId}
                                    >
                                      {DELIVERY_STATUSES.map((s) => (
                                        <option key={s} value={s}>
                                          {s}
                                        </option>
                                      ))}
                                    </select>

                                    <button
                                      className="btn btn-success btn-sm px-4 fw-medium rounded-3"
                                      onClick={() =>
                                        updateDeliveryStatus(d.deliveryId)
                                      }
                                      disabled={
                                        updatingId === d.deliveryId ||
                                        (statusDraft[d.deliveryId] ||
                                          d.status) === d.status
                                      }
                                    >
                                      {updatingId === d.deliveryId ? (
                                        <>
                                          <span className="spinner-border spinner-border-sm me-2" />
                                          Updating
                                        </>
                                      ) : (
                                        "Update Status"
                                      )}
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Pagination */}
              <div className="d-flex flex-column flex-sm-row justify-content-between align-items-center mt-3 gap-2">
                <div className="text-muted small">
                  Showing {deliveries.length ? (page - 1) * limit + 1 : 0}–
                  {Math.min(page * limit, total)} of {total} records
                </div>
                <Pagination
                  page={page}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  disabled={loading}
                />
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}

export default AdminDeliveries;
